import { ethers } from 'ethers';
import {
  getNetworkConfig, ORDER_TYPE, ARBITRUM_CHAIN_ID,
  INCREASE_ORDER_GAS_LIMIT, EXECUTION_FEE_FALLBACK_WEI, EXECUTION_FEE_MIN_WEI,
  UI_FEE_RECEIVER, MARKET_SLIPPAGE, DECREASE_SLIPPAGE,
} from './constants';
import { getInjectedProvider } from '../../utils/injectedProvider';

// ── ABIs (minimal) ───────────────────────────────────────────────────────────

export const EXCHANGE_ROUTER_ABI = [
  'function multicall(bytes[] calldata data) external payable returns (bytes[] memory)',
  'function sendWnt(address receiver, uint256 amount) external payable',
  'function sendTokens(address token, address receiver, uint256 amount) external',
  'function createOrder(tuple(tuple(address receiver, address cancellationReceiver, address callbackContract, address uiFeeReceiver, address market, address initialCollateralToken, address[] swapPath) addresses, tuple(uint256 sizeDeltaUsd, uint256 initialCollateralDeltaAmount, uint256 triggerPrice, uint256 acceptablePrice, uint256 executionFee, uint256 callbackGasLimit, uint256 minOutputAmount, uint256 validFromTime) numbers, uint8 orderType, uint8 decreasePositionSwapType, bool isLong, bool shouldUnwrapNativeToken, bool autoCancel, bytes32 referralCode, bytes32[] dataList) params) external payable returns (bytes32)',
];

export const ERC20_ABI = [
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function balanceOf(address account) external view returns (uint256)',
  'function decimals() view returns (uint8)',
];

export const READER_ABI = [
  'function getAccountPositions(address dataStore, address account, uint256 start, uint256 end) external view returns (tuple(tuple(address account, address market, address collateralToken) addresses, tuple(uint256 sizeInUsd, uint256 sizeInTokens, uint256 collateralAmount, uint256 borrowingFactor, uint256 fundingFeeAmountPerSize, uint256 longTokenClaimableFundingAmountPerSize, uint256 shortTokenClaimableFundingAmountPerSize, uint256 increasedAtTime, uint256 decreasedAtTime, uint256 decreasedAtBlock) numbers, tuple(bool isLong) flags)[])',
];

// ── Price helpers ─────────────────────────────────────────────────────────────

// Convert USD price to GMX format: USD per atomic token unit × 1e30
// GMX stores price as priceUsd * 10^(30 - indexTokenDecimals)
// BTC (8 decimals): $64391 → 64391 × 10^22 ≈ 6.44e26
// ETH (18 decimals): $3000  → 3000  × 10^12 ≈ 3e15
export function priceToGmx(priceUsd, indexTokenDecimals = 18) {
  const precision = 30 - indexTokenDecimals; // BTC→22, ETH→12
  return ethers.utils.parseUnits(String(Math.round(priceUsd)), precision);
}

// Acceptable price with slippage
export function acceptablePrice(priceUsd, isLong, slippage = MARKET_SLIPPAGE, indexTokenDecimals = 18) {
  const slip = isLong
    ? priceUsd * (1 + slippage)
    : priceUsd * (1 - slippage);
  return priceToGmx(slip, indexTokenDecimals);
}

// ── Dynamic execution fee ─────────────────────────────────────────────────────

// GMX validates the execution fee against its OWN gas config in the DataStore, not
// against a guess. Underpay and createOrder reverts (or the keeper refuses and the
// order is cancelled). The components, read live:
//   base amount + INCREASE_ORDER_GAS_LIMIT + oracle prices x per-price amount
// On Arbitrum today that is ~4.13M gas, where this file previously assumed 1.8M.
const DATASTORE_ABI = ['function getUint(bytes32) view returns (uint256)'];
const dsKey = (name) =>
  ethers.utils.keccak256(ethers.utils.defaultAbiCoder.encode(['string'], [name]));
const ORACLE_PRICE_COUNT = 3;        // index token + long token + short token
let _gasCfgCache = null;             // { ts, limit } — config changes rarely

async function gmxGasLimit(provider, chainId) {
  if (_gasCfgCache && Date.now() - _gasCfgCache.ts < 10 * 60 * 1000) return _gasCfgCache.limit;
  const { contracts } = getNetworkConfig(chainId);
  const ds = new ethers.Contract(contracts.DataStore, DATASTORE_ABI, provider);
  const [base, order, perPrice] = await Promise.all([
    ds.getUint(dsKey('ESTIMATED_GAS_FEE_BASE_AMOUNT_V2_1')),
    ds.getUint(dsKey('INCREASE_ORDER_GAS_LIMIT')),
    ds.getUint(dsKey('ESTIMATED_GAS_FEE_PER_ORACLE_PRICE')),
  ]);
  const limit = base.add(order).add(perPrice.mul(ORACLE_PRICE_COUNT));
  _gasCfgCache = { ts: Date.now(), limit };
  return limit;
}

export async function fetchExecutionFee(provider, chainId = ARBITRUM_CHAIN_ID) {
  try {
    const [gasPrice, gasLimit] = await Promise.all([
      provider.getGasPrice(),
      gmxGasLimit(provider, chainId).catch(() => ethers.BigNumber.from(INCREASE_ORDER_GAS_LIMIT)),
    ]);
    // 30% buffer: the keeper executes a block or two later, and GMX refunds the rest
    const fee = gasPrice.mul(gasLimit).mul(130).div(100);
    const floor = ethers.BigNumber.from(EXECUTION_FEE_MIN_WEI);
    return fee.gt(floor) ? fee : floor;
  } catch {
    return ethers.BigNumber.from(EXECUTION_FEE_FALLBACK_WEI);
  }
}


// ── Allowance check & approve ─────────────────────────────────────────────────

export async function ensureUsdcApproved(signer, usdcAmountRaw, chainId) {
  const { contracts, tokens } = getNetworkConfig(chainId);
  const usdc = new ethers.Contract(tokens.USDC.address, ERC20_ABI, signer);
  const owner = await signer.getAddress();
  // Spender is Router, not ExchangeRouter: sendTokens moves collateral via
  // Router.pluginTransfer, so the allowance has to sit on Router.
  const spender = contracts.Router;
  const current = await usdc.allowance(owner, spender);
  if (current.lt(usdcAmountRaw)) {
    const tx = await usdc.approve(spender, usdcAmountRaw);
    await tx.wait();
    // Wait for RPC to propagate the new allowance before proceeding
    await new Promise(r => setTimeout(r, 1500));
    const confirmed = await usdc.allowance(owner, spender);
    if (confirmed.lt(usdcAmountRaw)) {
      throw new Error('USDC approval did not confirm — please try again');
    }
  }
}

// ── Order construction ────────────────────────────────────────────────────────

export function buildMarketIncreaseOrder({
  account,
  marketToken,
  longToken,
  collateralToken,
  collateralAmountRaw,
  sizeDeltaUsd,
  currentPriceUsd,
  isLong,
  executionFeeWei,
  indexTokenDecimals = 18,
  isLimit = false,
  triggerPriceUsd = 0,
}) {
  return {
    addresses: {
      receiver:               account,
      cancellationReceiver:   account,
      callbackContract:       ethers.constants.AddressZero,
      uiFeeReceiver:          UI_FEE_RECEIVER,
      market:                 marketToken,
      initialCollateralToken: collateralToken,
      swapPath:               [],
    },
    numbers: {
      sizeDeltaUsd,
      initialCollateralDeltaAmount: collateralAmountRaw,
      triggerPrice:    isLimit ? priceToGmx(triggerPriceUsd, indexTokenDecimals) : ethers.BigNumber.from(0),
      acceptablePrice: acceptablePrice(currentPriceUsd, isLong, MARKET_SLIPPAGE, indexTokenDecimals),
      executionFee:    executionFeeWei,
      callbackGasLimit:  ethers.BigNumber.from(0),
      minOutputAmount:   ethers.BigNumber.from(0),
      validFromTime:     ethers.BigNumber.from(0),
    },
    orderType:                isLimit ? ORDER_TYPE.LimitIncrease : ORDER_TYPE.MarketIncrease,
    decreasePositionSwapType: 0,
    isLong,
    shouldUnwrapNativeToken:  false,
    autoCancel:               false,
    referralCode:             ethers.constants.HashZero,
    dataList:                 [],
  };
}

// ── Execute order via multicall ───────────────────────────────────────────────

export async function executeGmxOrder(signer, orderParams, collateralToken, collateralAmountRaw, executionFeeWei, chainId) {
  const { contracts } = getNetworkConfig(chainId);
  const router = new ethers.Contract(contracts.ExchangeRouter, EXCHANGE_ROUTER_ABI, signer);
  const iface  = router.interface;

  const multicallData = [
    iface.encodeFunctionData('sendWnt',     [contracts.OrderVault, executionFeeWei]),
    iface.encodeFunctionData('sendTokens',  [collateralToken, contracts.OrderVault, collateralAmountRaw]),
    iface.encodeFunctionData('createOrder', [orderParams]),
  ];

  // Use a public Arbitrum RPC for callStatic — wallet RPC and Alchemy both strip revert data
  const ARB_PUBLIC_RPC = chainId === 421614
    ? 'https://sepolia-rollup.arbitrum.io/rpc'
    : 'https://arb1.arbitrum.io/rpc';
  try {
    // StaticJsonRpcProvider + pinned network: no chain-detection calls (public RPC rate-limits them)
    const simProv    = new ethers.providers.StaticJsonRpcProvider(ARB_PUBLIC_RPC,
      chainId === 421614 ? { chainId: 421614, name: 'arbitrum-sepolia' } : { chainId: 42161, name: 'arbitrum' });
    const signerAddr = await signer.getAddress();
    const routerSim  = new ethers.Contract(contracts.ExchangeRouter, EXCHANGE_ROUTER_ABI, simProv);
    await routerSim.callStatic.multicall(multicallData, { value: executionFeeWei, from: signerAddr });
  } catch (simErr) {
    // Specific revert info first; ethers' generic "missing revert data…" reason
    // is NOT a real reason — treat it as no-info and fall through to the real tx
    const raw = simErr.data || simErr.error?.data || '';
    let reason = null;
    if (simErr.errorName) {
      reason = simErr.errorArgs?.length ? `${simErr.errorName}(${simErr.errorArgs.join(', ')})` : simErr.errorName;
    } else if (typeof raw === 'string' && raw.startsWith('0x08c379a0')) {
      try { reason = ethers.utils.defaultAbiCoder.decode(['string'], '0x' + raw.slice(10))[0]; } catch { reason = raw.slice(0, 20); }
    } else if (typeof raw === 'string' && raw.length > 10) {
      reason = `sel:${raw.slice(0, 10)}`;
    } else if (simErr.reason && !/missing revert data|reverted without a reason/i.test(simErr.reason)) {
      reason = simErr.reason;
    }
    if (!reason) {
      // No usable revert info (RPC stripped it or flaked) — skip the simulation
      // gate; a genuine failure will surface from the real transaction
      console.warn('[gmxUtils] simulation gave no revert data, proceeding to real tx:', simErr.message?.slice(0, 120));
      const tx = await router.multicall(multicallData, { value: executionFeeWei, gasLimit: 3_000_000 });
      return tx;
    }
    throw new Error(`GMX: ${reason}`);
  }

  const tx = await router.multicall(multicallData, { value: executionFeeWei, gasLimit: 3_000_000 });
  return tx;
}

// ── USDC balance ──────────────────────────────────────────────────────────────

export async function getUsdcBalance(provider, address, chainId) {
  const { tokens } = getNetworkConfig(chainId);
  const usdc = new ethers.Contract(tokens.USDC.address, ERC20_ABI, provider);
  const bal  = await usdc.balanceOf(address);
  return parseFloat(ethers.utils.formatUnits(bal, 6));
}

// ── Network switch helper ─────────────────────────────────────────────────────

export async function switchToChain(chainId) {
  const injected = getInjectedProvider();
  if (!injected) throw new Error('No wallet provider available');
  await injected.request({
    method: 'wallet_switchEthereumChain',
    params: [{ chainId: '0x' + chainId.toString(16) }],
  });
}

// ── Open positions reader ─────────────────────────────────────────────────────

export async function fetchOpenPositions(provider, account, chainId, markets) {
  try {
    const { contracts } = getNetworkConfig(chainId);
    const reader = new ethers.Contract(contracts.Reader, READER_ABI, provider);
    const raw = await reader.getAccountPositions(contracts.DataStore, account, 0, 20);

    return raw
      .filter(p => p.numbers.sizeInUsd.gt(0))
      .map(p => {
        const marketEntry = Object.entries(markets).find(
          ([, m]) => m.marketToken.toLowerCase() === p.addresses.market.toLowerCase()
        );
        const marketKey = marketEntry?.[0] || p.addresses.market.slice(0, 8) + '…';
        const mkt = marketEntry?.[1];

        const sizeUsd = parseFloat(ethers.utils.formatUnits(p.numbers.sizeInUsd, 30));
        const collateralUsd = parseFloat(ethers.utils.formatUnits(p.numbers.collateralAmount, 6));
        const indexDecimals = marketKey === 'BTC/USD' ? 8 : 18;
        const sizeInTokens = parseFloat(ethers.utils.formatUnits(p.numbers.sizeInTokens, indexDecimals));
        const entryPrice = sizeInTokens > 0 ? sizeUsd / sizeInTokens : 0;

        return {
          key:             `${p.addresses.market}-${p.flags.isLong}`,
          marketKey,
          mkt,
          marketToken:     p.addresses.market,
          collateralToken: p.addresses.collateralToken,
          isLong:          p.flags.isLong,
          sizeInUsdRaw:    p.numbers.sizeInUsd,
          sizeUsd,
          collateralUsd,
          entryPrice,
          leverage: collateralUsd > 0 ? Math.round(sizeUsd / collateralUsd) : 0,
        };
      });
  } catch (err) {
    console.error('[gmxUtils] fetchOpenPositions failed:', err);
    return [];
  }
}

// ── Decrease (close) order ────────────────────────────────────────────────────

export function buildMarketDecreaseOrder({
  account,
  marketToken,
  collateralToken,
  sizeDeltaUsd,
  currentPriceUsd,
  isLong,
  executionFeeWei,
  indexTokenDecimals = 18,
}) {
  return {
    addresses: {
      receiver:               account,
      cancellationReceiver:   account,
      callbackContract:       ethers.constants.AddressZero,
      uiFeeReceiver:          UI_FEE_RECEIVER,
      market:                 marketToken,
      initialCollateralToken: collateralToken,
      swapPath:               [],
    },
    numbers: {
      sizeDeltaUsd,
      initialCollateralDeltaAmount: ethers.BigNumber.from(0),
      triggerPrice:    ethers.BigNumber.from(0),
      acceptablePrice: acceptablePrice(currentPriceUsd, !isLong, DECREASE_SLIPPAGE, indexTokenDecimals),
      executionFee:    executionFeeWei,
      callbackGasLimit:  ethers.BigNumber.from(0),
      minOutputAmount:   ethers.BigNumber.from(0),
      validFromTime:     ethers.BigNumber.from(0),
    },
    orderType:                ORDER_TYPE.MarketDecrease,
    decreasePositionSwapType: 0,
    isLong,
    shouldUnwrapNativeToken:  false,
    autoCancel:               false,
    referralCode:             ethers.constants.HashZero,
    dataList:                 [],
  };
}

export async function executeGmxDecreaseOrder(signer, orderParams, executionFeeWei, chainId) {
  const { contracts } = getNetworkConfig(chainId);
  const router = new ethers.Contract(contracts.ExchangeRouter, EXCHANGE_ROUTER_ABI, signer);
  const iface  = router.interface;

  const multicallData = [
    iface.encodeFunctionData('sendWnt',     [contracts.OrderVault, executionFeeWei]),
    iface.encodeFunctionData('createOrder', [orderParams]),
  ];

  // Pre-flight simulation to surface revert reasons before sending real tx
  const ARB_PUBLIC_RPC = chainId === 421614
    ? 'https://sepolia-rollup.arbitrum.io/rpc'
    : 'https://arb1.arbitrum.io/rpc';
  try {
    // StaticJsonRpcProvider + pinned network: no chain-detection calls (public RPC rate-limits them)
    const simProv    = new ethers.providers.StaticJsonRpcProvider(ARB_PUBLIC_RPC,
      chainId === 421614 ? { chainId: 421614, name: 'arbitrum-sepolia' } : { chainId: 42161, name: 'arbitrum' });
    const signerAddr = await signer.getAddress();
    const routerSim  = new ethers.Contract(contracts.ExchangeRouter, EXCHANGE_ROUTER_ABI, simProv);
    await routerSim.callStatic.multicall(multicallData, { value: executionFeeWei, from: signerAddr });
  } catch (simErr) {
    // Specific revert info first; ethers' generic "missing revert data…" reason
    // is NOT a real reason — treat it as no-info and fall through to the real tx
    const raw = simErr.data || simErr.error?.data || '';
    let reason = null;
    if (simErr.errorName) {
      reason = simErr.errorArgs?.length ? `${simErr.errorName}(${simErr.errorArgs.join(', ')})` : simErr.errorName;
    } else if (typeof raw === 'string' && raw.startsWith('0x08c379a0')) {
      try { reason = ethers.utils.defaultAbiCoder.decode(['string'], '0x' + raw.slice(10))[0]; } catch { reason = raw.slice(0, 20); }
    } else if (typeof raw === 'string' && raw.length > 10) {
      reason = `sel:${raw.slice(0, 10)}`;
    } else if (simErr.reason && !/missing revert data|reverted without a reason/i.test(simErr.reason)) {
      reason = simErr.reason;
    }
    if (!reason) {
      console.warn('[gmxUtils] decrease simulation gave no revert data, proceeding to real tx:', simErr.message?.slice(0, 120));
      const tx = await router.multicall(multicallData, { value: executionFeeWei, gasLimit: 3_000_000 });
      return tx;
    }
    throw new Error(`GMX: ${reason}`);
  }

  const tx = await router.multicall(multicallData, { value: executionFeeWei, gasLimit: 3_000_000 });
  return tx;
}
